/**
 * Shared Zod Schemas
 * Centralized validation schemas for consistent form validation across the app
 */

import { z } from "zod";
import { getPasswordError } from "./passwordRules";

// ============================================
// PRIMITIVE FIELD SCHEMAS
// ============================================

/**
 * Phone number schema
 * Accepts: (123) 456-7890, 123-456-7890, +1 123 456 7890, etc.
 */
export const phoneSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === "") return true;
      const digitsOnly = val.replace(/\D/g, "");
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    },
    { message: "Please enter a valid phone number (7-15 digits)" }
  );

/**
 * Required phone number schema
 */
export const phoneRequiredSchema = z
  .string()
  .min(1, "Phone number is required")
  .refine(
    (val) => {
      const digitsOnly = val.replace(/\D/g, "");
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    },
    { message: "Please enter a valid phone number (7-15 digits)" }
  );

/**
 * Email schema (optional)
 */
export const emailSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === "") return true;
      return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val);
    },
    { message: "Please enter a valid email address" }
  );

/**
 * Required email schema
 */
export const emailRequiredSchema = z
  .string()
  .min(1, "Email is required")
  .email("Please enter a valid email address");

/**
 * Password schema with strength requirements
 */
export const passwordSchema = z.string().superRefine((val, ctx) => {
  // Same rules, order, messages and symbol set as the auth service.
  const error = getPasswordError(val);
  if (error) ctx.addIssue({ code: "custom", message: error });
});

/**
 * Simple password schema (min length only)
 */
export const passwordSimpleSchema = z
  .string()
  .min(8, "Password must be at least 8 characters");

/**
 * URL schema (optional)
 */
export const urlSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === "") return true;
      try {
        new URL(val.startsWith("http") ? val : `https://${val}`);
        return true;
      } catch {
        return false;
      }
    },
    { message: "Please enter a valid URL" }
  );

/**
 * Date schema (YYYY-MM-DD format)
 */
export const dateSchema = z.string().refine(
  (val) => {
    if (!val) return true;
    const date = new Date(val);
    return !isNaN(date.getTime());
  },
  { message: "Please enter a valid date" }
);

/**
 * Required date schema
 */
export const dateRequiredSchema = z
  .string()
  .min(1, "Date is required")
  .refine(
    (val) => {
      const date = new Date(val);
      return !isNaN(date.getTime());
    },
    { message: "Please enter a valid date" }
  );

/**
 * Date of birth schema with age validation
 */
export const dateOfBirthSchema = (minAge = 0, maxAge = 100) =>
  z
    .string()
    .min(1, "Date of birth is required")
    .refine(
      (val) => {
        const date = new Date(val);
        return !isNaN(date.getTime());
      },
      { message: "Please enter a valid date" }
    )
    .refine(
      (val) => {
        const date = new Date(val);
        const today = new Date();
        return date <= today;
      },
      { message: "Date of birth cannot be in the future" }
    )
    .refine(
      (val) => {
        const date = new Date(val);
        const today = new Date();
        const age = Math.floor(
          (today.getTime() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
        );
        return age >= minAge;
      },
      { message: `Must be at least ${minAge} years old` }
    )
    .refine(
      (val) => {
        const date = new Date(val);
        const today = new Date();
        const age = Math.floor(
          (today.getTime() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
        );
        return age <= maxAge;
      },
      { message: `Age cannot exceed ${maxAge} years` }
    );

/**
 * Currency/money schema (non-negative number).
 * Uses Zod coercion so it accepts both numbers and the Decimal-as-string
 * values the API returns (e.g. "50.00"); empty input fails as "required".
 */
export const currencySchema = z.coerce
  .number({ message: "Please enter a valid amount" })
  .min(0, "Amount cannot be negative");

/**
 * Positive integer schema
 */
export const positiveIntSchema = z
  .number()
  .int("Must be a whole number")
  .positive("Must be greater than 0");

/**
 * Percentage schema (0-100)
 */
export const percentageSchema = z
  .number()
  .min(0, "Percentage cannot be negative")
  .max(100, "Percentage cannot exceed 100");

// ============================================
// COMPOSITE FIELD SCHEMAS
// ============================================

/**
 * Address schema
 */
export const addressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().max(3, "Use country code (e.g., USA)").optional(),
});

/**
 * Required address schema
 */
export const addressRequiredSchema = z.object({
  street: z.string().min(1, "Street address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postal_code: z.string().min(1, "Postal code is required"),
  country: z
    .string()
    .min(1, "Country is required")
    .max(3, "Use country code (e.g., USA)"),
});

/**
 * Name schema (first name, last name)
 */
export const nameSchema = z.object({
  first_name: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name is too long"),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name is too long"),
});

/**
 * Contact info schema
 */
export const contactSchema = z.object({
  email: emailRequiredSchema,
  phone: phoneSchema,
});

// ============================================
// ENTITY SCHEMAS
// ============================================

/**
 * User creation schema
 */
export const createUserSchema = z.object({
  email: emailRequiredSchema,
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: phoneSchema,
  role: z.enum(
    ["super_user", "admin", "principal", "staff", "teacher", "parent"],
    { message: "Please select a role" }
  ),
});

/**
 * Student schema
 */
export const studentSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  date_of_birth: dateOfBirthSchema(2, 30),
  gender: z.enum(["male", "female", "other"]).optional(),
  student_number: z.string().optional(),
  allergies: z.string().optional(),
  medical_notes: z.string().optional(),
});

/**
 * Campus schema
 */
export const campusSchema = z.object({
  name: z.string().min(1, "Campus name is required"),
  code: z
    .string()
    .min(1, "Campus code is required")
    .max(10, "Campus code is too long"),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().max(3, "Use country code (e.g., USA)").optional(),
  phone: phoneSchema,
  email: emailSchema,
  website: urlSchema,
  status: z.enum(["active", "inactive"]).default("active"),
});

/**
 * Class schema
 */
export const classSchema = z.object({
  campus_id: z.string().min(1, "Please select a campus"),
  name: z.string().min(1, "Class name is required"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(
      /^[a-z0-9-]+$/,
      "Slug can only contain lowercase letters, numbers, and hyphens"
    ),
  description: z.string().optional(),
  start_date: dateRequiredSchema,
  end_date: dateRequiredSchema,
  capacity: positiveIntSchema,
  class_type: z.enum(["regular", "hifdh"]).default("regular"),
  meeting_days: z.array(z.number().min(0).max(6)).optional(),
  class_start_time: z.string().optional(),
  class_end_time: z.string().optional(),
});

/**
 * Login schema
 */
export const loginSchema = z.object({
  email: emailRequiredSchema,
  password: z.string().min(1, "Password is required"),
  remember_me: z.boolean().optional(),
});

/**
 * Change password schema
 */
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, "Current password is required"),
    new_password: passwordSchema,
    confirm_password: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });

/**
 * Reset password schema
 */
export const resetPasswordSchema = z
  .object({
    new_password: passwordSchema,
    confirm_password: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });

// ============================================
// HELPER TYPES
// ============================================

export type CreateUserFormData = z.infer<typeof createUserSchema>;
export type StudentFormData = z.infer<typeof studentSchema>;
export type CampusFormData = z.infer<typeof campusSchema>;
export type ClassFormData = z.infer<typeof classSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
